import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useEffect, useState } from "react";
import TaxModal from "./components/tax-modal";
import GroupModal from "./components/group-modal";
import TTax from "./types/TTax";
import TTaxGroup from "./types/TTaxGroup";
import ToggleSwitch from "components/toggle/ToggleSwitch";
import { FaArchive } from "react-icons/fa";

const TaxManagement = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TTaxGroup | null>(null);
  const [selectedTax, setSelectedTax] = useState<TTax | null>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedTaxes, setSelectedTaxes] = useState<number[]>([]);
  const [showArchivedTaxes, setShowArchivedTaxes] = useState(false);
  const [showArchivedGroups, setShowArchivedGroups] = useState(false);
  const [searchQueryTaxes, setSearchQueryTaxes] = useState("");
  const [searchQueryGroups, setSearchQueryGroups] = useState("");

  const queryClient = useQueryClient();

  const getTaxes = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/dashboard`
    );
    return response.data;
  };

  const { data, isLoading, isError, error } = useQuery("taxes", getTaxes, { refetchOnWindowFocus: false });

  const mutation = useMutation(
    (newGroup: { label: string; taxIds: number[] }) => {
      if (isEditMode && selectedGroup) {
        return axios.put(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups`,
          {
            id: selectedGroup.id,
            label: newGroup.label,
            taxIds: newGroup.taxIds,
          }
        );
      } else {
        return axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups`,
          newGroup
        );
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("taxes");
        setIsGroupModalOpen(false);
        setSelectedTaxes([]);
        setGroupName("");
        setSelectedGroup(null);
        setIsEditMode(false);
      },
      onError: (err) => {
        console.error("Failed to save tax group:", err);
      },
    }
  );

  const taxes = (data?.taxes as TTax[] ?? []).sort((a, b) => a.label.localeCompare(b.label));
  const taxGroups = (data?.taxGroups as TTaxGroup[] ?? []).sort((a, b) => a.label.localeCompare(b.label));

  const toggleGroupModal = (group?: TTaxGroup) => {
    if (group) {
      setIsEditMode(true);
      setGroupName(group.label);
      setSelectedTaxes(group.taxes.map((tax: TTax) => tax.id).filter((id): id is number => id !== undefined));
      setSelectedGroup(group);
    } else {
      setIsEditMode(false);
      setGroupName("");
      setSelectedTaxes([]);
      setSelectedGroup(null);
    }
    setIsGroupModalOpen((prev) => !prev);
  };

  const toggleTaxModal = (tax?: TTax) => {
    if (tax) {
      setSelectedTax(tax);
    } else {
      setSelectedTax(null);
    }
    setIsTaxModalOpen((prev) => !prev);
  };

  const handleTaxSelection = (taxId: number) => {
    setSelectedTaxes((prevSelected) =>
      prevSelected.includes(Number(taxId))
        ? prevSelected.filter((id) => Number(id) !== Number(taxId))
        : [...prevSelected, Number(taxId)]
    );
  };

  const handleSaveGroup = async () => {
    if (groupName && selectedTaxes.length > 0) {
      mutation.mutate({ label: groupName, taxIds: selectedTaxes });
    }
  };

  const filteredTaxes = taxes
    .filter((tax) =>
      (showArchivedTaxes || !tax.archived) &&
      tax.label.toLowerCase().includes(searchQueryTaxes.toLowerCase())
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  const filteredTaxGroups = taxGroups
    .filter((group) =>
      (showArchivedGroups || !group.archived) &&
      group.label.toLowerCase().includes(searchQueryGroups.toLowerCase())
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Tax Management</h1>
      <button
        onClick={() => toggleTaxModal()}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mr-4"
      >
        New Item
      </button>
      <button
        onClick={() => toggleGroupModal()}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        New Group
      </button>

      {isLoading ? (
        <div className="text-center text-gray-600">Loading...</div>
      ) : (
        <div className="flex gap-6 h-[600px]">

          {/* Taxes Section (Left Side) */}
          <div className="w-1/2 flex flex-col border border-gray-300 rounded-lg shadow-md p-4 overflow-auto">
            <h2 className="text-xl font-semibold mb-2">Taxes</h2>

            {/* Filters for Taxes */}
            <div className="flex items-center mb-4 space-x-4">
              <input
                type="text"
                placeholder="Search taxes..."
                value={searchQueryTaxes}
                onChange={(e) => setSearchQueryTaxes(e.target.value)}
                className="p-2 border border-gray-300 rounded w-64"
              />
              <ToggleSwitch
                checked={showArchivedTaxes}
                onChange={() => setShowArchivedTaxes((prev) => !prev)}
                label="Show Archived"
              />
            </div>

            <div className="space-y-4">
              {filteredTaxes.map((tax) => {
                const rates = [
                  { label: "List", rate: tax.list_rate },
                  { label: "Sale", rate: tax.sale_rate },
                  { label: "Install", rate: tax.install_rate },
                ].filter((r) => r.rate !== undefined && r.rate !== null);

                const uniqueRates = new Set(rates.map((r) => Number(r.rate)));

                return (
                  <div key={tax.id} className="relative p-4 bg-white rounded-lg shadow-md border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">{tax.label}</span>
                      <button className="text-blue-500 hover:underline" onClick={() => toggleTaxModal(tax)}>
                        Edit
                      </button>
                    </div>

                    {/* Rate Display Logic */}
                    <div className="text-gray-500">
                      {uniqueRates.size === 1 ? (
                        <span>
                          Rate: {tax.list_type === "fixed"
                            ? `$${(Number([...uniqueRates][0]) / 100).toFixed(2)}`
                            : `${Number([...uniqueRates][0])}%`}
                        </span>
                      ) : (
                        rates.map((r) => (
                          (r.rate > 0 || r.label !== 'List') && (
                            <div key={r.label}>
                              {r.label} Rate: {tax.list_type === "fixed"
                                ? `$${(Number(r.rate) / 100).toFixed(2)}`
                                : `${Number(r.rate)}%`}
                            </div>
                          )
                        ))
                      )}
                    </div>

                    <div className="text-gray-500">Type: {tax.list_type}</div>
                    <div className="text-gray-500">Category: {tax.tax_type}</div>

                    {tax.archived && <FaArchive className="absolute bottom-2 right-2 text-red-500 w-5 h-5" />}
                  </div>
                );
              })}
            </div>

          </div>

          {/* Tax Groups Section (Right Side) */}
          <div className="w-1/2 flex flex-col border border-gray-300 rounded-lg shadow-md p-4 overflow-auto">
            <h2 className="text-xl font-semibold mb-2">Tax Groups</h2>

            {/* Filters for Tax Groups */}
            <div className="flex items-center mb-4 space-x-4">
              <input
                type="text"
                placeholder="Search tax groups..."
                value={searchQueryGroups}
                onChange={(e) => setSearchQueryGroups(e.target.value)}
                className="p-2 border border-gray-300 rounded w-64"
              />
              <ToggleSwitch
                checked={showArchivedGroups}
                onChange={() => setShowArchivedGroups((prev) => !prev)}
                label="Show Archived"
              />
            </div>

            <div className="space-y-4">
              {filteredTaxGroups.map((taxGroup) => (
                <div key={taxGroup.id} className="relative p-4 bg-white rounded-lg shadow-md border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{taxGroup.label}</span>
                    <button
                      className="text-blue-500 hover:underline"
                      onClick={() => toggleGroupModal(taxGroup)}
                    >
                      Edit
                    </button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {taxGroup.taxes.map((tax) => (
                      <div key={tax.id} className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="font-medium text-gray-700">{tax.label}</div>
                        <div className="text-gray-500">Rate: {tax.list_rate}%</div>
                        <div className="text-gray-500">Type: {tax.list_type}</div>
                        <div className="text-gray-500">Category: {tax.tax_type}</div>
                      </div>
                    ))}
                  </div>

                  {/* Fixed Red Archive Icon */}
                  {taxGroup.archived && (
                    <FaArchive className="absolute bottom-2 right-2 text-red-500 w-5 h-5" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isTaxModalOpen && (
        <TaxModal
          taxes={taxes}
          isTaxModalOpen={isTaxModalOpen}
          setIsTaxModalOpen={setIsTaxModalOpen}
          selectedTax={selectedTax}
          setSelectedTax={setSelectedTax}
        />
      )}

      {isGroupModalOpen && (
        <GroupModal
          taxes={taxes}
          groupName={groupName}
          setGroupName={setGroupName}
          selectedTaxes={selectedTaxes}
          handleTaxSelection={handleTaxSelection}
          handleSaveGroup={handleSaveGroup}
          isEditMode={isEditMode}
          setIsGroupModalOpen={setIsGroupModalOpen}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
        />
      )}
    </div >
  )
};

export default TaxManagement;